import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, Divider, Flex, Icon, Link, Popover, Text } from 'core/components';
import { PopoverInteractionKind } from '@blueprintjs/core';
import { Collection } from 'core/model';
import AdminTable from 'app/components/admin/AdminTable';
import CopyToClipboardButton from 'app/components/CopyToClipboardButton';
import DeviceLink from 'app/components/links/DeviceLink';
import InterfaceLink from 'app/components/links/InterfaceLink';
import SiteLink from 'app/components/links/SiteLink';
import { greekIt } from 'app/util/utils';
import { buildMtrRows, AlternateIpInfo, MtrDeviceInfo, TraceInterface } from './tracerouteTable/buildMtrRows';
import { formatMtrClipboard, getMtrCsvData } from './tracerouteTable/formatMtrExport';

// Typed wrappers for injected JS components (inject provides $app at runtime)
interface InterfaceLinkProps {
  snmp_id?: string;
  interface_description?: string;
  device_name?: string;
  children?: React.ReactNode;
}
// @ts-expect-error - $app is injected at runtime by mobx-react inject()
const TypedInterfaceLink = (props: InterfaceLinkProps) => <InterfaceLink {...props} />;

interface SiteLinkProps {
  siteId: string;
  children?: React.ReactNode;
}
// @ts-expect-error - $app is injected at runtime by mobx-react inject()
const TypedSiteLink = (props: SiteLinkProps) => <SiteLink {...props} />;

interface TracerouteTableProps {
  traceroutes: unknown[];
  selectedAgents: string[];
  selectedIps: string[];
  isPreview: boolean;
  $exports?: { exportCsv: (opts: { columns: string[]; data: unknown[][]; fileName: string }) => void };
}

const TableWrapper = styled(Box)`
  font-size: 13px;
`;

const HoverableIp = styled.span`
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  cursor: default;
`;

function formatRtt(value: number | null): string {
  if (value == null || value <= 0) {
    return '*';
  }
  return value < 1 ? '<1' : value.toFixed(2);
}

// Loss coloring: only applied when the path has downstream loss
function getLossStyle(lossPercent: number, hasDownstreamLoss: boolean): { color?: string; bg?: string } {
  if (!hasDownstreamLoss || lossPercent === 0) {
    return {};
  }
  if (lossPercent >= 50) {
    return { color: '#c0392b', bg: 'rgba(192, 57, 43, 0.1)' };
  }
  return { color: '#e67e22', bg: 'rgba(230, 126, 34, 0.1)' };
}

function formatLossPercent(sent: number, recv: number): string {
  if (sent === 0) {
    return '0%';
  }
  const lost = sent - recv;
  const pct = Math.round((lost / sent) * 100);
  return `${pct}%`;
}

function formatLossCounts(sent: number, recv: number): string {
  if (sent === 0) {
    return '0/0';
  }
  const lost = sent - recv;
  return `${lost}/${sent}`;
}

type MtrModel = { get: (key: string) => unknown };

// --- RTT Popover Content ---
function RttPopoverContent({ pings }: { pings: (number | null)[] }) {
  return (
    <Box p={1} maxHeight={200} overflow="auto">
      <Text as="div" fontWeight="bold" mb="4px">
        Per-Probe RTT
      </Text>
      {/* -- probes are positional, no stable ID */}
      {/* eslint-disable-next-line react/no-array-index-key */}
      {pings.map((ping, probeIndex) => (
        <Text as="div" key={probeIndex} small>
          Probe {probeIndex + 1}: {ping != null ? `${ping.toFixed(2)} ms` : '*'}
        </Text>
      ))}
    </Box>
  );
}

// --- Device Popover Content ---
function DevicePopoverContent({ device }: { device: MtrDeviceInfo }) {
  const { ingress, egress } = device;

  const renderInterface = (intf: TraceInterface, label: string) => {
    const capacity = (intf.interface_speed || 0) * 1000000;
    const connectivityType = intf.interface_connectivity_type || '';

    return (
      <Box flex="1 50%" px={1}>
        <Text as="div" muted mb="4px">
          {label}
        </Text>
        <Text as="div" my="2px">
          <TypedInterfaceLink
            snmp_id={intf.snmp_id}
            interface_description={intf.interface_name || ''}
            device_name={device.deviceName}
          />
        </Text>
        {!!capacity && (
          <Text as="div" my="2px">
            Capacity: {greekIt(capacity, { fix: 0 }).displayFull}
          </Text>
        )}
        {connectivityType && (
          <Text as="div" my="2px">
            Type: {connectivityType}
          </Text>
        )}
      </Box>
    );
  };

  return (
    <Box p={2} minWidth={260} maxWidth={400}>
      <Text as="div" mb="4px">
        More Info
      </Text>
      <Text muted small>
        Device and Interface Statistics are for current time
        <br />
        (not aggregated/historical)
      </Text>
      {(device.deviceName || device.site) && (
        <>
          <Box my={1}>
            <Divider />
          </Box>
          <Flex mx={-1}>
            {device.deviceName && (
              <Box flex="1 50%" px={1}>
                <Text as="div" muted mb="4px">
                  Device
                </Text>
                <Text as="div" my="2px">
                  <DeviceLink name={device.deviceName} />
                </Text>
                {device.deviceMetrics?.cpu_busy != null && (
                  <Text as="div" my="2px">
                    CPU: {device.deviceMetrics.cpu_busy}%
                  </Text>
                )}
                {device.deviceMetrics?.mem_used != null && (
                  <Text as="div" my="2px">
                    Mem: {device.deviceMetrics.mem_used}%
                  </Text>
                )}
              </Box>
            )}
            {device.site?.id && (
              <Box flex="1 50%" px={1}>
                <Text as="div" muted mb="4px">
                  Site
                </Text>
                <Text as="div" my="2px">
                  <TypedSiteLink siteId={device.site.id}>{device.site.name}</TypedSiteLink>
                </Text>
              </Box>
            )}
          </Flex>
        </>
      )}
      {(ingress || egress) && (
        <>
          <Box my={1}>
            <Divider />
          </Box>
          <Flex mx={-1}>
            {ingress && renderInterface(ingress, ingress && egress ? 'Ingress' : 'Interface')}
            {egress && renderInterface(egress, ingress && egress ? 'Egress' : 'Interface')}
          </Flex>
        </>
      )}
    </Box>
  );
}

// --- Alternate IP Expandable Row ---
function HostCell({ model }: { model: MtrModel }) {
  const [expanded, setExpanded] = useState(false);
  const ip = model.get('ip') as string;
  const hostname = model.get('hostname') as string;
  const alternateIps = model.get('alternateIps') as AlternateIpInfo[];
  const device = model.get('device') as MtrDeviceInfo | null;

  const ipDisplay = ip === '*' ? '*' : ip;

  const primaryContent = (
    <Box>
      <Flex alignItems="center" gap="4px">
        {device ? (
          <Popover
            interactionKind={PopoverInteractionKind.HOVER}
            hoverOpenDelay={200}
            hoverCloseDelay={100}
            position="right"
            minimal={false}
            content={<DevicePopoverContent device={device} />}
          >
            <HoverableIp>{ipDisplay}</HoverableIp>
          </Popover>
        ) : (
          <span>{ipDisplay}</span>
        )}
      </Flex>
      {hostname !== '-' && hostname !== '*' && (
        <Text as="div" muted small>
          {hostname}
        </Text>
      )}
      {device && (
        <Text as="div" muted small>
          <DeviceLink name={device.deviceName} />
          {(() => {
            const intf = device.ingress || device.egress;
            if (!intf?.interface_name) return null;
            return (
              <>
                {' — '}
                <TypedInterfaceLink
                  snmp_id={intf.snmp_id}
                  interface_description={intf.interface_name}
                  device_name={device.deviceName}
                />
              </>
            );
          })()}
        </Text>
      )}
    </Box>
  );

  if (!alternateIps.length) {
    return primaryContent;
  }

  return (
    <Box>
      {primaryContent}
      <Flex mt="2px" cursor="pointer" userSelect="none" onClick={() => setExpanded(!expanded)}>
        <Text small muted>
          {expanded ? '▾' : '▸'} {alternateIps.length} alternate IP{alternateIps.length > 1 ? 's' : ''}
        </Text>
      </Flex>
      {expanded &&
        alternateIps.map((alt) => (
          <Box key={alt.ip} ml={2} mt="2px" pl={1} borderLeft="2px solid #ccc">
            <strong>{alt.ip}</strong>
            {alt.hostname !== '-' && (
              <Text as="div" muted small>
                {alt.hostname}
              </Text>
            )}
          </Box>
        ))}
    </Box>
  );
}

const columns = [
  {
    label: 'Hop',
    name: 'hop',
    width: 60,
    align: 'right',
    headerAlign: 'left',
    paddingRight: 25,
    renderer: ({ value }: { value: number }) => <>{value}</>
  },
  {
    label: 'Max RTT (ms)',
    name: 'maxRtt',
    width: 110,
    align: 'right',
    headerAlign: 'left',
    paddingRight: 25,
    ellipsis: false,
    renderer: ({ model }: { model: MtrModel }) => {
      const maxRtt = model.get('maxRtt') as number | null;
      const pings = model.get('pings') as (number | null)[];
      return (
        <Popover
          interactionKind={PopoverInteractionKind.HOVER}
          hoverOpenDelay={200}
          hoverCloseDelay={100}
          position="top"
          minimal={false}
          content={<RttPopoverContent pings={pings} />}
        >
          <span style={{ cursor: 'default' }}>{formatRtt(maxRtt)}</span>
        </Popover>
      );
    }
  },
  {
    label: (
      <Flex alignItems="center" gap="4px">
        Packet Loss
        <Popover
          interactionKind={PopoverInteractionKind.HOVER}
          hoverOpenDelay={200}
          hoverCloseDelay={100}
          position="top"
          minimal={false}
          content={
            <Box p={1} maxWidth={300}>
              <Text small>
                Cells are highlighted only when packet loss originates at a hop and continues through all subsequent
                hops, indicating a genuine network degradation rather than ICMP rate-limiting.
              </Text>
            </Box>
          }
        >
          <Icon iconSize={12} icon="info-sign" color="muted" />
        </Popover>
      </Flex>
    ),
    name: 'lossPercent',
    width: 130,
    align: 'right',
    headerAlign: 'left',
    paddingRight: 25,
    ellipsis: false,
    renderer: ({ model }: { model: MtrModel }) => {
      const lossPercent = model.get('lossPercent') as number;
      const sent = model.get('sent') as number;
      const recv = model.get('recv') as number;
      const hasDownstreamLoss = model.get('hasDownstreamLoss') as boolean;
      const style = getLossStyle(lossPercent, hasDownstreamLoss);
      return (
        <Popover
          interactionKind={PopoverInteractionKind.HOVER}
          hoverOpenDelay={200}
          hoverCloseDelay={100}
          position="top"
          minimal={false}
          content={
            <Box p={1}>
              <Text small>{formatLossCounts(sent, recv)} packets lost</Text>
            </Box>
          }
        >
          <Box px="4px" py="2px" borderRadius={3} bg={style.bg} display="inline-block">
            <Text color={style.color}>{formatLossPercent(sent, recv)}</Text>
          </Box>
        </Popover>
      );
    }
  },
  {
    label: 'Host',
    name: 'ip',
    width: 300,
    padding: '6px 0px 6px 22px',
    ellipsis: false,
    renderer: ({ model }: { model: MtrModel }) => <HostCell model={model} />
  },
  {
    label: 'ASN',
    name: 'asnId',
    width: 160,
    padding: '6px 0px 6px 22px',
    ellipsis: false,
    renderer: ({ model }: { model: MtrModel }) => {
      const asnId = model.get('asnId') as string;
      const asnName = model.get('asnName') as string;
      const alternateIps = model.get('alternateIps') as AlternateIpInfo[];

      // Show additional ASNs only if they differ from primary
      const diffAsns = alternateIps.filter((alt) => alt.asnId && alt.asnId !== asnId);
      const uniqueDiffAsns = diffAsns.filter((alt, i, arr) => arr.findIndex((a) => a.asnId === alt.asnId) === i);

      return (
        <Box>
          {asnId ? `AS${asnId}` : '-'}
          {asnName && (
            <Text as="div" small>
              <Link to={`/v4/core/quick-views/asn/${asnId}/traffic`} target="_blank">
                {asnName}
              </Link>
            </Text>
          )}
          {uniqueDiffAsns.map((alt) => (
            <Box key={alt.asnId} mt="2px">
              <Text small muted>
                AS{alt.asnId}
              </Text>
              {alt.asnName && (
                <Text as="div" small>
                  <Link to={`/v4/core/quick-views/asn/${alt.asnId}/traffic`} target="_blank">
                    {alt.asnName}
                  </Link>
                </Text>
              )}
            </Box>
          ))}
        </Box>
      );
    }
  },
  {
    label: 'Location',
    name: 'city',
    width: 200,
    padding: '6px 0px 6px 22px',
    ellipsis: false,
    renderer: ({ model }: { model: MtrModel }) => {
      const city = model.get('city') as string;
      const country = model.get('country') as string;
      const alternateIps = model.get('alternateIps') as AlternateIpInfo[];

      const primaryLocation = [city, country].filter(Boolean).join(', ') || '-';

      // Show alternate locations only if they differ
      const diffLocations = alternateIps
        .map((alt) => [alt.city, alt.country].filter(Boolean).join(', '))
        .filter((loc) => loc && loc !== primaryLocation);
      const uniqueLocations = [...new Set(diffLocations)];

      return (
        <Box>
          {primaryLocation}
          {uniqueLocations.map((loc) => (
            <Text as="div" key={loc} small muted mt="2px">
              {loc}
            </Text>
          ))}
        </Box>
      );
    }
  }
];

const TracerouteTable = ({ traceroutes, selectedAgents, selectedIps, isPreview, $exports }: TracerouteTableProps) => {
  const { collection, rows } = useMemo(() => {
    const traceIndex = isPreview ? traceroutes.length - 1 : 0;
    const traceroute = traceroutes[traceIndex] as Record<string, unknown> | undefined;
    if (!traceroute?.traces) {
      return { collection: new Collection(), rows: [] };
    }

    const traces = traceroute.traces as Array<{
      agentId: number;
      target?: { target_ip?: string };
      probes?: Array<{ hops: unknown[]; completed: boolean }>;
    }>;

    // Filter by both selected agent and selected target IP.
    // The store may return traces for multiple agents (e.g. initial load fetches 5).
    // In table mode we show one agent + one target, so filter to match the form selection.
    const trace = traces.find(({ agentId, target }) => {
      const matchAgent = !selectedAgents.length || selectedAgents.includes(String(agentId));
      const matchTarget = !selectedIps.length || (target?.target_ip != null && selectedIps.includes(target.target_ip));
      return matchAgent && matchTarget;
    });

    if (!trace?.probes) {
      return { collection: new Collection(), rows: [] };
    }

    const mtrRows = buildMtrRows(trace.probes as Parameters<typeof buildMtrRows>[0]);

    // Collection requires models with an `id` field
    const modelsWithId = mtrRows.map((row) => ({ ...row, id: row.hop }));
    const coll = new Collection(modelsWithId, { sortState: { field: 'hop', direction: 'asc' } });

    return { collection: coll, rows: mtrRows };
  }, [traceroutes, selectedAgents, selectedIps, isPreview]);

  if (!rows.length) {
    return (
      <Flex height={200} alignItems="center" justifyContent="center">
        <Text>
          Not enough data to construct a trace. Agents that time out before the trace can complete will show no hop
          data. Please check the Advanced Settings for this test.
        </Text>
      </Flex>
    );
  }

  return (
    <Box>
      <Flex justifyContent="flex-end" mb={1} gap={1}>
        <CopyToClipboardButton text={formatMtrClipboard(rows)}>
          <Button icon="clipboard">Copy to Clipboard</Button>
        </CopyToClipboardButton>
        {$exports && (
          <Button
            icon="download"
            onClick={() => {
              const { columns: csvColumns, data } = getMtrCsvData(rows);
              $exports.exportCsv({ columns: csvColumns, data, fileName: 'traceroute-mtr' });
            }}
          >
            Download CSV
          </Button>
        )}
      </Flex>
      <TableWrapper>
        <AdminTable
          collection={collection}
          columns={columns}
          fetchCollectionOnMount={false}
          searchBar={false}
          hideResultCount
          noVirtualizedTable
          tabPrefId="tracerouteTable"
        />
      </TableWrapper>
    </Box>
  );
};

export default TracerouteTable;
